import React from 'react'
import { cm } from '~/utils/utils'

export type InputAreaSize = 'small' | 'default' | 'large'

export interface InputAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  size?: InputAreaSize
  isDisabled?: boolean
  singleLine?: boolean
  fullWidth?: boolean
  value: string
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  type?: 'text' | 'password'
}

const inputSizeClasses: Record<InputAreaSize, string> = {
  small: 'h-[36px] rounded-lg w-[240px]',
  default: 'h-[48px] rounded-xl w-[320px]',
  large: 'h-[64px] rounded-xl w-[320px]'
}

const textareaSizeClasses: Record<InputAreaSize, string> = {
  small: 'min-h-[36px] rounded-lg w-[240px]',
  default: 'min-h-[48px] rounded-xl w-[320px]',
  large: 'min-h-[64px] rounded-xl w-[320px]'
}

export const InputArea = React.forwardRef<HTMLTextAreaElement, InputAreaProps>(
  (
    {
      size = 'default',
      className,
      isDisabled,
      placeholder,
      value,
      onChange,
      singleLine = false,
      fullWidth = false,
      onFocus,
      onBlur,
      type = 'text',
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false)

    const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true)
      onFocus?.(event)
    }

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false)
      onBlur?.(event)
    }

    const baseClasses = cm(
      // Base styles
      'px-3 py-2 rounded-xl text-base font-normal leading-4',
      // Placeholder styles
      'placeholder:text-[rgba(0,0,0,0.25)] placeholder:text-base placeholder:font-normal placeholder:leading-4',
      'dark:placeholder:text-tokens-neutral-text-tertiary',
      // Input text color
      'text-black/88 dark:text-tokens-neutral-text-primary',
      // Size styles
      inputSizeClasses[size],
      singleLine && ['py-[8px]', 'whitespace-nowrap overflow-hidden'],
      // Default state styles
      'border border-[#D6D6D6] bg-white dark:border-tokens-neutral-border-2 dark:bg-tokens-neutral-bg-grey-2',
      // Focus state styles
      'focus:border-[#846FE1] focus:outline-none',
      // Disabled state styles
      isDisabled && [
        'border-[#D6D6D6] bg-[rgba(0,0,0,0.04)]',
        'dark:bg-tokens-neutral-bg-disabled dark:text-tokens-neutral-text-disabled',
        'text-[rgba(38,38,38,0.20)]',
        'cursor-not-allowed'
      ],
      fullWidth && 'w-full',
      className
    )

    if (singleLine) {
      return (
        <input
          ref={ref as React.Ref<HTMLInputElement>}
          type={type}
          className={baseClasses}
          disabled={isDisabled}
          placeholder={placeholder}
          value={value}
          onChange={onChange as unknown as (event: React.ChangeEvent<HTMLInputElement>) => void}
          onFocus={handleFocus as unknown as (event: React.FocusEvent<HTMLInputElement>) => void}
          onBlur={handleBlur as unknown as (event: React.FocusEvent<HTMLInputElement>) => void}
          {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
        />
      )
    }

    return (
      <textarea
        ref={ref}
        className={cm(
          // Base styles
          'text-base font-normal leading-5 resize-none',
          // Placeholder styles
          'placeholder:text-[rgba(0,0,0,0.25)] placeholder:text-base placeholder:font-normal placeholder:leading-5',
          'dark:placeholder:text-tokens-neutral-text-tertiary',
          // Input text color
          'text-[rgba(0,0,0,0.88)] dark:text-tokens-neutral-text-primary',
          // Size styles (includes padding)
          textareaSizeClasses[size],
          'px-3 py-3',
          'whitespace-pre-wrap break-words',
          // Default state styles
          'border border-[#D6D6D6] bg-white dark:border-tokens-neutral-border-2 dark:bg-tokens-neutral-bg-grey-2',
          // Focus state styles
          'focus:border-[#846FE1] focus:outline-none',
          // Shadow when there is content and the field is focused
          value.trim() && isFocused && 'shadow-toast',
          // Allow toggling the CSS focus state while debugging in DevTools
          value.trim() && 'focus:shadow-toast',
          // Disabled state styles
          isDisabled && [
            'border-[#D6D6D6] bg-[rgba(0,0,0,0.04)]',
            'dark:bg-tokens-neutral-bg-disabled dark:text-tokens-neutral-text-disabled',
            'text-[rgba(38,38,38,0.20)]',
            'cursor-not-allowed'
          ],
          fullWidth && 'w-full',
          className
        )}
        disabled={isDisabled}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      />
    )
  }
)

InputArea.displayName = 'InputArea'
